import { useEffect, useMemo } from "react";

import { BarChart3, Clock, FileText, Upload } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import Button from "../../Components/Button";
import Table from "../../Components/Table";
import WorldMap from "../../Components/WorldMap";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchLevelMix,
  LevelMixLevel,
  LevelMixAccount,
} from "../../Store/Reducers/AssessmentSlice";
import {
  fetchCommandCenterMetrics,
  fetchCommandCenterAccountsNeedSponsors,
  fetchCommandCenterSponsorsOnBench,
  fetchCommandCenterAccountSurveys,
  fetchAccountsWithSponsors,
  FetchAccountsFilters,
  fetchUploadStats,
  AccountSurveyItem,
} from "../../Store/Reducers/OpsSlice";
import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import { formatDateUSShort } from "../../Utils/Helper/Helper";
import {
  getAccountLevelCompactLabel,
  parseLevelNumber,
} from "../../Utils/Helper/levelUtils";
import {
  transformAccountsForWorldMap,
  getAccountCountsByStatus,
} from "../../Utils/Helper/WorldMapHelpers";

import "./style.scss";

const isAccountSurveyCompleted = (item: AccountSurveyItem) =>
  item.status === "completed";

const isAccountSurveyOverdue = (item: AccountSurveyItem) => {
  if (isAccountSurveyCompleted(item)) return false;
  if (item.status === "overdue") return true;
  const dueDate = item.due ? new Date(item.due) : null;
  return Boolean(dueDate && dueDate < new Date());
};

const isAccountSurveyOngoing = (item: AccountSurveyItem) =>
  !isAccountSurveyCompleted(item) && !isAccountSurveyOverdue(item);

// Badge colors for alignment levels
const LEVEL_BADGE_COLORS: Record<number, { bg: string; text: string }> = {
  0: { bg: "#e5e7eb", text: "#6b7280" },
  1: { bg: "#fecaca", text: "#991b1b" },
  2: { bg: "#fed7aa", text: "#92400e" },
  3: { bg: "#fef3c7", text: "#78350f" },
  4: { bg: "#bfdbfe", text: "#1e40af" },
  5: { bg: "#bbf7d0", text: "#166534" },
  6: { bg: "#e9d5ff", text: "#6b21a8" },
  7: { bg: "#93c5fd", text: "#1e3a8a" },
};

const CommandCenterContainer = () => {
  const dispatch = useAppDispatch();
  const { formatTier } = useAccountTiers();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: metricsData } = useAppSelector(
    state => state.ops.commandCenterMetrics
  );
  const {
    data: accountsNeedSponsorsData,
    loading: accountsNeedSponsorsLoading,
  } = useAppSelector(state => state.ops.commandCenterAccountsNeedSponsors);
  const { data: sponsorsOnBenchData, loading: sponsorsOnBenchLoading } =
    useAppSelector(state => state.ops.commandCenterSponsorsOnBench);
  const { data: accountSurveysData, loading: accountSurveysLoading } =
    useAppSelector(state => state.ops.commandCenterAccountSurveys);
  const { data: accountsDataResponse } = useAppSelector(
    state => state.ops.accounts
  );
  const { data: uploadStatsData, loading: uploadStatsLoading } = useAppSelector(
    state => state.ops.uploadStats
  );
  const { levelMix, isLoadingLevelMix } = useAppSelector(
    state => state.assessment
  );

  // Level mix date range - Format: YYYY-MM-DD
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const levelMixDateRange = {
    startDate: formatDateForAPI(
      new Date(new Date().setMonth(new Date().getMonth() - 1))
    ),
    endDate: formatDateForAPI(new Date()),
  };

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchCommandCenterMetrics({}));
    dispatch(fetchCommandCenterAccountsNeedSponsors({}));
    dispatch(fetchCommandCenterSponsorsOnBench({}));
    dispatch(fetchCommandCenterAccountSurveys({}));
    const filters: FetchAccountsFilters = {};
    dispatch(fetchAccountsWithSponsors(filters));
    dispatch(fetchUploadStats());
    dispatch(
      fetchLevelMix({
        startDate: levelMixDateRange.startDate,
        endDate: levelMixDateRange.endDate,
        scope: "formal",
      })
    );
  }, [dispatch, levelMixDateRange.startDate, levelMixDateRange.endDate]);

  // Navigation handlers
  const navigateToTab = (tabId: string, assessmentTab?: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tabId);
    if (assessmentTab) {
      newParams.set("assessmentTab", assessmentTab);
    }
    setSearchParams(newParams);
  };

  // Metrics with progress bars
  const metrics = useMemo(() => {
    const totalAccounts = metricsData?.totalAccounts ?? 0;
    const accountsWithSponsors = metricsData?.accountsWithSponsors ?? 0;
    const accountsWithoutSponsors = metricsData?.accountsWithoutSponsors ?? 0;
    const totalSponsors = metricsData?.totalSponsors ?? 0;
    const sponsorsOnBench = metricsData?.sponsorsOnBench ?? 0;
    const pendingSponsorRequest = metricsData?.pendingSponsorRequest ?? 0;
    const avgSponsorCSAT = metricsData?.avgSponsorCSAT ?? 0;

    return [
      {
        title: (
          <>
            Total
            <br />
            Accounts
          </>
        ),
        value: totalAccounts,
        progress: 100,
        color: "blue",
      },
      {
        title: (
          <>
            Accounts With
            <br />
            Sponsors
          </>
        ),
        value: accountsWithSponsors,
        progress:
          totalAccounts > 0
            ? Math.round((accountsWithSponsors / totalAccounts) * 100)
            : 0,
        color: "blue",
      },
      {
        title: (
          <>
            Accounts Without
            <br />
            Sponsors
          </>
        ),
        value: accountsWithoutSponsors,
        progress:
          totalAccounts > 0
            ? Math.round((accountsWithoutSponsors / totalAccounts) * 100)
            : 0,
        color: "blue",
      },
      {
        title: (
          <>
            Total
            <br />
            Sponsors
          </>
        ),
        value: totalSponsors,
        progress: 100,
        color: "green",
      },
      {
        title: (
          <>
            Sponsors on
            <br />
            Bench
          </>
        ),
        value: sponsorsOnBench,
        progress:
          totalSponsors > 0
            ? Math.round((sponsorsOnBench / totalSponsors) * 100)
            : 0,
        color: "green",
      },
      {
        title: (
          <>
            Pending Assignment
            <br />
            Requests
          </>
        ),
        value: pendingSponsorRequest,
        progress:
          totalSponsors > 0
            ? Math.round((pendingSponsorRequest / totalSponsors) * 100)
            : 0,
        color: "green",
      },
      {
        title: (
          <>
            Avg. Sponsor
            <br />
            CSAT
          </>
        ),
        value: avgSponsorCSAT > 0 ? avgSponsorCSAT : "Coming Soon",
        progress: avgSponsorCSAT > 0 ? avgSponsorCSAT : 0,
        color: "green",
        isComingSoon: avgSponsorCSAT === 0 || !avgSponsorCSAT,
      },
    ];
  }, [metricsData]);

  // Format "Unassigned Since" as "Dec 1, 2025 (91 days)"
  const formatUnassignedSince = (dateString: string): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const raisedAt = new Date(date);
      raisedAt.setHours(0, 0, 0, 0);
      const diffTime = today.getTime() - raisedAt.getTime();
      const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return `${formattedDate} (${days} days)`;
    } catch {
      return "N/A";
    }
  };

  // Map accounts need sponsors data
  const mappedAccountsNeedSponsorsData = useMemo(() => {
    if (!accountsNeedSponsorsData) return [];
    return accountsNeedSponsorsData.map(item => {
      // Extract region and tier from category or level if available
      const region = (item as any).region || "N/A";
      const tier = (item as any).tier || "N/A";

      return {
        account: item.account,
        region: region,
        tier: tier === "N/A" ? tier : formatTier(tier) || tier,
        unassignedSince: item.raisedAt
          ? formatUnassignedSince(item.raisedAt)
          : "N/A",
        action: (
          <Button
            variant="clear"
            className="primary-color text-sm"
            onClick={() => navigateToTab("assignments-activations")}
          >
            Assign Sponsor
          </Button>
        ),
      };
    });
  }, [accountsNeedSponsorsData, formatTier]);

  // Map sponsors with available capacity data
  const mappedSponsorsWithCapacityData = useMemo(() => {
    if (!sponsorsOnBenchData) return [];
    return sponsorsOnBenchData.map(item => {
      const experienceLevel = item.experienceLevel || "N/A";
      const region = item.region || "N/A";
      const utilization = item.utilization ?? 0;

      return {
        sponsor: item.sponsor,
        experienceLevel: experienceLevel,
        region: region,
        availableCapacity: item.availableCapacity || 0,
        utilization: `${utilization}%`,
        utilizationColor:
          utilization === 0
            ? "text-green-600"
            : utilization < 50
              ? "text-yellow-600"
              : "text-red-600",
      };
    });
  }, [sponsorsOnBenchData]);

  // Map account assessment data
  const mappedAccountAssessmentData = useMemo(() => {
    if (!accountSurveysData) return [];
    return accountSurveysData.map(item => {
      const dueDate = item.due ? new Date(item.due) : null;
      const isOverdue = isAccountSurveyOverdue(item);
      const progress = item.progress ?? 0;
      const total = item.total ?? item.sentTo ?? 1;

      return {
        account: item.account,
        progress: (
          <div className="flex items-center gap-2">
            <div className="w-24 bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${(progress / total) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600">
              {progress}/{total}
            </span>
          </div>
        ),
        dueDate: dueDate ? (
          <span
            className={`text-sm flex items-center gap-1 ${
              isOverdue ? "text-red-600" : "text-gray-600"
            }`}
          >
            {isOverdue && <Clock className="h-3 w-3" />}
            {formatDateUSShort(item.due, true)}
          </span>
        ) : (
          "N/A"
        ),
        status: (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              item.status === "overdue" || isOverdue
                ? "bg-red-100 text-red-800"
                : item.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : item.status === "ongoing"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800"
            }`}
          >
            {item.status === "overdue" || isOverdue
              ? "Overdue"
              : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </span>
        ),
        action: (
          <Button
            variant="clear"
            className="primary-color text-sm flex items-center w-full"
            onClick={() => {
              navigateToTab("assessments");
            }}
          >
            View
          </Button>
        ),
      };
    });
  }, [accountSurveysData]);

  // Calculate assessment summary stats
  const assessmentStats = useMemo(() => {
    if (!accountSurveysData) {
      return { recent: 0, completed: 0, ongoing: 0, overdue: 0 };
    }
    return {
      recent: accountSurveysData.length,
      completed: accountSurveysData.filter(isAccountSurveyCompleted).length,
      ongoing: accountSurveysData.filter(isAccountSurveyOngoing).length,
      overdue: accountSurveysData.filter(isAccountSurveyOverdue).length,
    };
  }, [accountSurveysData]);

  // Map alignment levels data - Dynamic based on API response
  const mappedAlignmentLevelsData = useMemo(() => {
    // levelMix is LevelMixResponse which has a 'levels' property
    const levels = levelMix?.levels || [];

    if (!Array.isArray(levels) || levels.length === 0) {
      return [];
    }

    // Calculate total accounts (L0–L5 only — Unknown is not in pie %)
    const assessedLevels = levels.filter(
      l =>
        String(l.level || "")
          .trim()
          .toLowerCase() !== "unknown"
    );
    const totalAccounts = assessedLevels.reduce(
      (sum, l) => sum + (l.count || l.accounts?.length || 0),
      0
    );

    // Map only the levels that come from the API
    return levels.map((levelData: LevelMixLevel) => {
      const isUnknown =
        String(levelData.level || "")
          .trim()
          .toLowerCase() === "unknown";
      const accountCount = levelData.count || levelData.accounts?.length || 0;
      const percentage =
        levelData.percentage !== null && levelData.percentage !== undefined
          ? levelData.percentage
          : !isUnknown && totalAccounts > 0
            ? Math.round((accountCount / totalAccounts) * 100)
            : null;

      const accountNames =
        levelData.accounts && levelData.accounts.length > 0
          ? levelData.accounts
              .slice(0, 2)
              .map((acc: LevelMixAccount) => acc.name)
          : [];

      // Get badge colors for inline styles based on level
      const levelNum = parseLevelNumber(levelData.level);
      const colors = isUnknown
        ? { bg: "#F3F4F6", text: "#4B5563" }
        : LEVEL_BADGE_COLORS[levelNum] || LEVEL_BADGE_COLORS[0];

      const levelDisplayName = isUnknown
        ? levelData.levelName || levelData.level || ""
        : getAccountLevelCompactLabel(levelData.level, levelData.levelName);

      return {
        level: (
          <span
            className="px-2 py-1 rounded text-xs font-medium"
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
            }}
          >
            {levelDisplayName}
          </span>
        ),
        accountCount: accountCount,
        percentage:
          percentage !== null && accountCount > 0 ? `${percentage}%` : "—",
        accounts:
          accountNames.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {accountNames.map((name: string, idx: number) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium"
                >
                  {name}
                </span>
              ))}
            </div>
          ) : (
            "—"
          ),
      };
    });
  }, [levelMix]);

  // Format level mix date range for display
  const formattedDateRange = useMemo(() => {
    const start = new Date(levelMixDateRange.startDate + "T00:00:00");
    const end = new Date(levelMixDateRange.endDate + "T00:00:00");
    return `${start.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    })} - ${end.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    })}`;
  }, [levelMixDateRange]);

  // World Map Data
  const accounts = useMemo(() => {
    if (!accountsDataResponse?.items) return [];
    return accountsDataResponse.items;
  }, [accountsDataResponse]);

  const accountsData = useMemo(() => {
    return transformAccountsForWorldMap(accounts);
  }, [accounts]);

  // Upload stats processing
  const uploadStats = useMemo(() => {
    if (!uploadStatsData) return null;

    return {
      pendingReviews: uploadStatsData.pendingCount || 0,
      awaitingActivation: uploadStatsData.approvedCount || 0,
      recentBatches: uploadStatsData.recentBatches || [],
    };
  }, [uploadStatsData]);

  // Check if upload type is a bulk upload type (users, customers, or composite)
  const isBulkUploadType = (uploadType: string | undefined): boolean => {
    return (
      uploadType === "users" ||
      uploadType === "customers" ||
      uploadType === "composite"
    );
  };

  // Format upload name based on upload type
  const formatUploadName = (
    name: string | undefined,
    uploadType: string | undefined
  ): string => {
    if (!name || !uploadType) {
      // Fallback based on upload type
      if (uploadType === "users") {
        return "Executives Uploaded";
      } else if (uploadType === "customers") {
        return "Accounts Added";
      }
      return name || "Upload";
    }

    // Remove date/time patterns from batch name to avoid duplication
    // Pattern matches: " - DD/MM/YYYY, HH:MM:SS" or similar date formats
    let formattedName = name
      .replace(/\s*-\s*\d{1,2}\/\d{1,2}\/\d{4}[,\s]*\d{1,2}:\d{2}:\d{2}.*$/, "")
      .trim();

    // Handle add operations - replace "Add" with "Added"
    if (uploadType === "add_customer" || uploadType === "add_user") {
      // Replace "Bulk Add" with "Bulk Added"
      formattedName = formattedName.replace(/\bBulk Add\b/gi, "Bulk Added");
      // Replace standalone "Add" (not part of "Added") with "Added"
      formattedName = formattedName.replace(/\bAdd\b(?!ded)/gi, "Added");
      // Replace "Executives Add" with "Executives Added"
      formattedName = formattedName.replace(
        /\bExecutives Add\b/gi,
        "Executives Added"
      );
      // Replace "Accounts Add" with "Accounts Added"
      formattedName = formattedName.replace(
        /\bAccounts Add\b/gi,
        "Accounts Added"
      );
    }

    // Team member edits (in-app update approval batches)
    if (
      uploadType === "update_account_team_members" ||
      uploadType === "update_customer_team_members"
    ) {
      formattedName = formattedName.replace(
        /\bAccount Team Added\b/gi,
        "Account Team Updated"
      );
      formattedName = formattedName.replace(
        /\bCustomer Team Added\b/gi,
        "Customer Team Updated"
      );
      formattedName = formattedName.replace(
        /\bEdit account team member\b/gi,
        "In-app account team update"
      );
      formattedName = formattedName.replace(
        /\bEdit customer team member\b/gi,
        "In-app customer team update"
      );
    }

    // Handle update operations - replace "Update" with "Updated"
    if (uploadType === "customer_update" || uploadType === "user_update") {
      // Replace "Bulk Update" with "Bulk Updated"
      formattedName = formattedName.replace(
        /\bBulk Update\b/gi,
        "Bulk Updated"
      );
      // Replace standalone "Update" (not part of "Updated") with "Updated"
      formattedName = formattedName.replace(/\bUpdate\b(?!d)/gi, "Updated");
      // Replace "Executives Update" with "Executives Updated"
      formattedName = formattedName.replace(
        /\bExecutives Update\b/gi,
        "Executives Updated"
      );
      // Replace "Accounts Update" with "Accounts Updated"
      formattedName = formattedName.replace(
        /\bAccounts Update\b/gi,
        "Accounts Updated"
      );
    }

    // Handle delete operations
    if (uploadType === "delete_customer") {
      // Replace "Delete" with "Deleted" if not already "Deleted"
      formattedName = formattedName.replace(/\bDelete\b(?!d)/gi, "Deleted");
      // Replace "Bulk Delete" with "Bulk Deleted"
      formattedName = formattedName.replace(
        /\bBulk Delete\b/gi,
        "Bulk Deleted"
      );
      // Replace "Account Delete" with "Account Deleted"
      formattedName = formattedName.replace(
        /\bAccount Delete\b/gi,
        "Account Deleted"
      );
    }

    // Handle bulk upload types (users, customers, composite)
    if (isBulkUploadType(uploadType)) {
      // If name contains "Add" but not "Added", replace with "Added"
      if (formattedName.match(/\bAdd\b(?!ded)/i)) {
        formattedName = formattedName.replace(/\bBulk Add\b/gi, "Bulk Added");
        formattedName = formattedName.replace(/\bAdd\b(?!ded)/gi, "Added");
      }
      // If name contains "Upload" but not "Uploaded", replace with "Uploaded"
      if (formattedName.match(/\bUpload\b(?!ed)/i)) {
        formattedName = formattedName.replace(
          /\bBulk Upload\b/gi,
          "Bulk Uploaded"
        );
        formattedName = formattedName.replace(/\bUpload\b(?!ed)/gi, "Uploaded");
      }
    }

    return formattedName;
  };

  return (
    <>
      <div className="ops-dashboard-container">
        <div className="mb-5 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex gap-6 overflow-x-auto metrics-scroll-container">
            {metrics.map((metric, index) => {
              const progressColor =
                metric.color === "blue"
                  ? "bg-blue-500"
                  : metric.color === "green"
                    ? "bg-green-500"
                    : "bg-gray-300";
              const progressBgColor =
                metric.color === "blue"
                  ? "bg-blue-100"
                  : metric.color === "green"
                    ? "bg-green-100"
                    : "bg-gray-100";

              return (
                <div
                  key={index}
                  className="bg-white rounded-lg flex flex-col flex-1"
                  style={{ minWidth: "161px", flex: "1 1 161px" }}
                >
                  <div className="text-sm font-medium text-gray-700 mb-3 break-words leading-tight">
                    {metric.title}
                  </div>
                  <div
                    className={`${metric.isComingSoon ? "text-xl" : "text-3xl"} font-bold mb-4 ${
                      metric.isComingSoon ? "text-gray-400" : "text-gray-900"
                    }`}
                  >
                    {metric.value}
                  </div>
                  <div className="mt-auto">
                    <div
                      className={`h-1 rounded-full ${progressBgColor} overflow-hidden`}
                    >
                      <div
                        className={`h-full ${progressColor} transition-all duration-300`}
                        style={{
                          width: `${Math.min(metric.progress, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Accounts and Uploads Grid Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Our Accounts
                </h3>
                <p className="text-sm text-gray-500 font-medium">
                  {accountsData.length} accounts
                </p>
              </div>
              <button
                onClick={() => navigateToTab("accounts")}
                className="text-purple-600 text-sm hover:text-purple-700 font-medium"
              >
                See All
              </button>
            </div>

            {/* Account Visualization - World Map Style */}
            <div className="relative h-80 bg-gray-50 rounded-lg mb-4 overflow-hidden">
              <WorldMap
                key="commandcenter-all-accounts"
                accounts={accountsData.slice(0, 10)}
                title="Global Account Distribution"
                size="md"
                showTitle={false}
                showFullViewButton={true}
                onNavigateToAccounts={() => navigateToTab("accounts")}
                allAccounts={accountsData}
              />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center space-x-6 text-sm">
              {(() => {
                const trueCounts = getAccountCountsByStatus(accountsData);
                return (
                  <>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                      <span className="text-gray-600">
                        Healthy ·{" "}
                        {trueCounts.healthy.toString().padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                      <span className="text-gray-600">
                        At Risk ·{" "}
                        {trueCounts.atRisk.toString().padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-gray-600">
                        Critical ·{" "}
                        {trueCounts.critical.toString().padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
                      <span className="text-gray-600">
                        Unassigned ·{" "}
                        {trueCounts.unassigned.toString().padStart(2, "0")}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Uploads and Approvals Widget */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Uploads and Approvals
                </h3>
                {uploadStats &&
                  (uploadStats.pendingReviews > 0 ||
                    uploadStats.awaitingActivation > 0) && (
                    <div className="ml-2 flex items-center gap-4">
                      {uploadStats.pendingReviews > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 rounded-full">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="text-xs font-medium text-yellow-700">
                            {uploadStats.pendingReviews} Pending Review
                            {uploadStats.pendingReviews !== 1 ? "s" : ""}
                          </span>
                        </div>
                      )}
                      {/* {uploadStats.awaitingActivation > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-indigo-100 rounded-full">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                          <span className="text-xs font-medium text-indigo-700">
                            {uploadStats.awaitingActivation} Awaiting Activation
                            {uploadStats.awaitingActivation !== 1 ? "s" : ""}
                          </span>
                        </div>
                      )} */}
                    </div>
                  )}
              </div>
              <div>
                <button
                  onClick={() => navigateToTab("data-upload")}
                  className="ml-1 text-purple-600 text-sm hover:text-purple-700 font-medium"
                >
                  See All
                </button>
              </div>
            </div>
            <div className="space-y-2 h-full">
              {uploadStatsLoading ? (
                <div className="flex flex-col items-center justify-center py-8 h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading...</p>
                </div>
              ) : uploadStats?.recentBatches &&
                uploadStats.recentBatches.length > 0 ? (
                uploadStats.recentBatches.slice(0, 6).map((batch, index) => {
                  const cleanBatchName = formatUploadName(
                    batch.batchName,
                    batch.uploadType
                  );
                  const status = batch.status as string;

                  // Format date and time: "DD MMM YYYY HH:MM:SS"
                  const uploadedDate = batch.uploadedAt
                    ? new Date(batch.uploadedAt)
                    : new Date();
                  const day = String(uploadedDate.getDate()).padStart(2, "0");
                  const month = uploadedDate.toLocaleDateString("en-GB", {
                    month: "short",
                  });
                  const year = uploadedDate.getFullYear();
                  const hours = String(uploadedDate.getHours()).padStart(
                    2,
                    "0"
                  );
                  const minutes = String(uploadedDate.getMinutes()).padStart(
                    2,
                    "0"
                  );
                  const seconds = String(uploadedDate.getSeconds()).padStart(
                    2,
                    "0"
                  );
                  const formattedDateTime = `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
                  const displayText = `${cleanBatchName} - ${formattedDateTime}`;

                  return (
                    <div
                      key={batch.id || index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            status === "activated"
                              ? "bg-green-500"
                              : status === "approved"
                                ? "bg-blue-500"
                                : status === "prep_activation"
                                  ? "bg-indigo-500"
                                  : status === "pending"
                                    ? "bg-yellow-500"
                                    : status === "cancelled"
                                      ? "bg-gray-500"
                                      : status === "rejected"
                                        ? "bg-red-500"
                                        : "bg-gray-400"
                          }`}
                        ></div>
                        <div className="flex flex-col flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-800">
                              {displayText}
                            </span>
                            <span
                              className={`text-xs font-medium ${
                                status === "activated"
                                  ? "text-green-600"
                                  : status === "approved"
                                    ? "text-blue-600"
                                    : status === "prep_activation"
                                      ? "text-indigo-600"
                                      : status === "pending"
                                        ? "text-yellow-600"
                                        : status === "cancelled"
                                          ? "text-gray-600"
                                          : status === "rejected"
                                            ? "text-red-600"
                                            : "text-gray-500"
                              }`}
                            >
                              Status:{" "}
                              {status === "prep_activation"
                                ? "Prep Activation"
                                : status.charAt(0).toUpperCase() +
                                  status.slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[200px] py-8 text-center text-gray-500">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className="font-medium text-gray-600">
                    No recent activity
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid - 4 Sections */}
        <div className="ops-main-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* 1. Accounts Needing Sponsors */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Accounts Needing Sponsors
                </h3>
                <Button
                  variant="clear"
                  className="primary-color text-sm"
                  onClick={() => navigateToTab("accounts")}
                >
                  See All
                </Button>
              </div>
              <Table
                loading={accountsNeedSponsorsLoading}
                columns={[
                  {
                    key: "account",
                    title: "Account",

                    render: (value: string) => {
                      return <div className="font-medium">{value || "-"}</div>;
                    },
                  },
                  { key: "region", title: "Region" },
                  { key: "tier", title: "Tier" },
                  { key: "unassignedSince", title: "Unassigned Since" },
                  {
                    key: "action",
                    title: "Action",
                    textalign: "center",
                  },
                ]}
                data={mappedAccountsNeedSponsorsData}
                emptyContent={
                  <div className="flex flex-col items-center">
                    <div className="font-medium">
                      All accounts have been assigned sponsors!
                    </div>
                    <div className="text-sm mt-1">
                      Great work on sponsor management.
                    </div>
                  </div>
                }
              />
            </div>

            {/* 2. Sponsors with Available Capacity */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Sponsors with Available Capacity
                </h3>
                <Button
                  variant="clear"
                  className="primary-color text-sm"
                  onClick={() => navigateToTab("executive-sponsors")}
                >
                  See All
                </Button>
              </div>
              <Table
                loading={sponsorsOnBenchLoading}
                columns={[
                  { key: "sponsor", title: "Sponsor" },
                  { key: "experienceLevel", title: "Experience Level" },
                  { key: "region", title: "Region" },
                  { key: "availableCapacity", title: "Available Capacity" },
                  {
                    key: "utilization",
                    title: "Utilization",
                    render: (value: string, row: any) => (
                      <span className={row.utilizationColor || "text-gray-600"}>
                        {value}
                      </span>
                    ),
                  },
                ]}
                data={mappedSponsorsWithCapacityData}
              />
            </div>

            {/* 3. Account Assessment */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Assessment
                </h3>
                <Button
                  variant="clear"
                  className="primary-color text-sm"
                  onClick={() => navigateToTab("assessments")}
                >
                  See All
                </Button>
              </div>
              <Table
                loading={accountSurveysLoading}
                columns={[
                  {
                    key: "account",
                    title: "Account",

                    render: (value: string) => {
                      return <div className="font-medium">{value || "-"}</div>;
                    },
                  },
                  { key: "progress", title: "Progress" },
                  { key: "dueDate", title: "Due Date" },
                  { key: "status", title: "Status" },
                  {
                    key: "action",
                    title: "Action",
                    textalign: "center",
                  },
                ]}
                data={mappedAccountAssessmentData}
                emptyContent={
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="font-medium">No assessments found</div>
                    <div className="text-sm mt-1">
                      Create your first assessment to get started
                    </div>
                  </div>
                }
              />
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 border-gray-200">
                <div className="flex flex-col items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-2xl font-bold text-blue-600">
                    {assessmentStats.recent}
                  </span>
                  <span className="text-xs text-blue-600 font-medium mt-1">
                    Recent
                  </span>
                </div>
                <div className="flex flex-col items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-2xl font-bold text-green-600">
                    {assessmentStats.completed}
                  </span>
                  <span className="text-xs text-green-600 font-medium mt-1">
                    Completed
                  </span>
                </div>
                <div className="flex flex-col items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-2xl font-bold text-blue-600">
                    {assessmentStats.ongoing}
                  </span>
                  <span className="text-xs text-blue-600 font-medium mt-1">
                    Ongoing
                  </span>
                </div>
                <div className="flex flex-col items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-2xl font-bold text-red-600">
                    {assessmentStats.overdue}
                  </span>
                  <span className="text-xs text-red-600 font-medium mt-1">
                    Overdue
                  </span>
                </div>
              </div>
            </div>

            {/* 4. Account Levels */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Account Levels
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    ({formattedDateRange})
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="clear"
                    className="primary-color text-sm"
                    onClick={() => navigateToTab("assessments", "level-mix")}
                  >
                    See All
                  </Button>
                </div>
              </div>
              <Table
                loading={isLoadingLevelMix}
                columns={[
                  { key: "level", title: "Account Level", width: "150px" },
                  { key: "accountCount", title: "No. Of Accounts" },
                  { key: "percentage", title: "% Of Accounts" },
                  {
                    key: "accounts",
                    title: "Accounts",
                    render: (value: any) => value,
                  },
                ]}
                data={mappedAlignmentLevelsData}
                emptyContent={
                  <div className="flex flex-col items-center">
                    <BarChart3 className="h-12 w-12 text-gray-400 mb-4" />
                    <div className="font-medium">
                      No level mix data found for this window.
                    </div>
                  </div>
                }
              />
              <p className="text-xs text-gray-500 mt-4 border-gray-200">
                Based on published assessments in the last 30 days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandCenterContainer;
